import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiError, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Shield, ShieldCheck, Users, Eye, EyeOff, Handshake, Loader2, Mail } from "lucide-react";

/**
 * Route accepts two shapes:
 *  - /admin/agencies/:agencyId/users  (super admin explicit target)
 *  - /agency/users                    (agency admin — resolves to their own)
 */
export default function AgencyUsers() {
  const { user: me } = useAuth();
  const { agencyId: routeAgencyId } = useParams();
  const nav = useNavigate();
  const [agencyId, setAgencyId] = useState(routeAgencyId || "");
  const [agency, setAgency] = useState(null);
  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: "create", editing: null });
  const [form, setForm] = useState({ email: "", name: "", role: "agent", password: "", visibility_scope: "assigned", assigned_dealer_ids: [] });
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = me?.role === "admin";
  const isAgencyAdmin = me?.role === "agency_admin";

  // Resolve agencyId when agency admin visits /agency/users.
  // Fallback via /auth/me if the login payload didn't include agency_id.
  useEffect(() => {
    if (routeAgencyId) return;
    if (!isAgencyAdmin) return;
    if (me?.agency_id) { setAgencyId(me.agency_id); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.agency_id) setAgencyId(data.agency_id);
      } catch (err) {
        console.error("[AgencyUsers] auth/me lookup failed:", err?.message || err);
      }
    })();
  }, [routeAgencyId, isAgencyAdmin, me]);

  const load = async () => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const [uRes, dRes] = await Promise.all([
        api.get(`/agency/${agencyId}/users`),
        api.get("/dealers").catch(() => ({ data: [] })),
      ]);
      setAgency(uRes.data.agency);
      setUsers(uRes.data.users || []);
      setDealers(dRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      if (e.response?.status === 403) nav("/dashboard");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [agencyId]);

  const hasAgencyAdmin = useMemo(() => users.some((u) => u.role === "agency_admin"), [users]);
  const canCreateAgencyAdmin = isSuperAdmin || hasAgencyAdmin;

  const openCreate = () => {
    setForm({ email: "", name: "", role: "agent", password: "", visibility_scope: "assigned", assigned_dealer_ids: [] });
    setDialog({ open: true, mode: "create", editing: null });
  };
  const openEdit = (u) => {
    setForm({
      email: u.email || "", name: u.name || "", role: u.role || "agent",
      password: "",
      visibility_scope: u.visibility_scope || "assigned",
      assigned_dealer_ids: u.assigned_dealer_ids || [],
      can_view_commissions: (u.permissions || []).includes("commissions.view"),
    });
    setDialog({ open: true, mode: "edit", editing: u });
  };

  const toggleDealerAssign = (dealerId) => {
    setForm((prev) => {
      const has = prev.assigned_dealer_ids.includes(dealerId);
      return { ...prev, assigned_dealer_ids: has ? prev.assigned_dealer_ids.filter((x) => x !== dealerId) : [...prev.assigned_dealer_ids, dealerId] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim()) { toast.error("Name and email required"); return; }
    if (dialog.mode === "create" && form.password.length < 6) { toast.error("Password must be 6+ chars"); return; }
    if (form.role === "agency_admin" && !canCreateAgencyAdmin) {
      toast.error("Only a Super Admin can create the first agency admin.");
      return;
    }
    // Agency admins always have full-agency visibility.
    const scope = form.role === "agency_admin" ? "all" : form.visibility_scope;
    const assigned = form.role === "agency_admin" ? [] : form.assigned_dealer_ids;
    setSaving(true);
    try {
      if (dialog.mode === "create") {
        const { data } = await api.post(`/agency/${agencyId}/users`, { ...form, visibility_scope: scope, assigned_dealer_ids: assigned });
        if (data?.welcome_emailed) toast.success(`User created — welcome email sent to ${data.email}`);
        else toast.warning(`User created, but the welcome email failed${data?.welcome_email_error ? `: ${data.welcome_email_error}` : ""}`);
      } else {
        const body = { ...form, visibility_scope: scope, assigned_dealer_ids: assigned };
        if (!body.password) delete body.password;
        await api.put(`/agency/${agencyId}/users/${dialog.editing.id}`, body);
        toast.success("User updated");
      }
      setDialog({ open: false, mode: "create", editing: null });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email} from ${agency?.name}?`)) return;
    try {
      await api.delete(`/agency/${agencyId}/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  if (!agencyId) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-10">
          <div className="text-sm text-[#6C757D]">Loading…</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-8">
          {isSuperAdmin && (
            <Link to="/admin/agencies" className="inline-flex items-center gap-1 text-xs text-[#6C757D] hover:text-[#0B132B] mb-3">
              <ArrowLeft size={12}/> Back to agencies
            </Link>
          )}
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            {isAgencyAdmin ? "Your Agency" : "Agency Administration"}
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] tracking-tight leading-none">{agency?.name || "Users"}</h1>
              <p className="text-sm text-[#6C757D] mt-3 max-w-xl leading-relaxed">
                Manage every user in this agency. Assign visibility scope per user and hand-pick dealers each agent can access.
              </p>
            </div>
            <Button onClick={openCreate} className="bg-[#0B132B] hover:bg-[#0B132B]/90 h-10" data-testid="agency-add-user-btn">
              <Plus size={15} className="mr-2" /> Add user
            </Button>
          </div>
          {!hasAgencyAdmin && isAgencyAdmin && (
            <div className="mt-4 rounded-sm border-2 border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 text-sm text-[#0B132B]">
              This agency has no Agency Admin yet. Ask a Super Admin to appoint the first one — after that, you can add more.
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-[#6C757D]">Loading…</div>
        ) : (
          <Card className="bg-white border border-[#E9ECEF] rounded-sm overflow-hidden" data-testid="agency-users-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0B132B] text-white text-xs uppercase tracking-[0.14em]">
                    <th className="text-left px-4 py-3 font-semibold">Name</th>
                    <th className="text-left px-4 py-3 font-semibold">Email</th>
                    <th className="text-left px-4 py-3 font-semibold">Role</th>
                    <th className="text-left px-4 py-3 font-semibold">Scope</th>
                    <th className="text-left px-4 py-3 font-semibold">Assigned Dealers</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA]" data-testid={`agency-user-row-${u.id}`}>
                      <td className="px-4 py-3 font-semibold text-[#0B132B]">{u.name}</td>
                      <td className="px-4 py-3 text-[#6C757D]">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] uppercase tracking-[0.14em] font-bold ${
                          u.role === "agency_admin" ? "bg-[#D4AF37]/20 text-[#0B132B]" : "bg-[#0B132B]/5 text-[#0B132B]"
                        }`}>
                          {u.role === "agency_admin" ? <><ShieldCheck size={11}/> Agency Admin</> : <><Users size={11}/> Agent</>}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs text-[#0B132B]">
                          {u.role === "agency_admin" ? (
                            <><Eye size={12}/> All agency data</>
                          ) : u.visibility_scope === "all" ? (
                            <><Eye size={12}/> All agency data</>
                          ) : (
                            <><EyeOff size={12}/> Assigned only</>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#6C757D]">
                        {u.role === "agency_admin" ? "—" : (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <Handshake size={12}/> {(u.assigned_dealer_ids || []).length} dealer(s)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="h-8 rounded-sm border-[#0B132B]/20 text-xs" data-testid={`agency-user-edit-${u.id}`}>
                            <Pencil size={12} className="mr-1"/> Edit
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 rounded-sm border-[#0B132B]/20 text-xs" data-testid={`agency-user-send-reset-${u.id}`}
                            onClick={async () => {
                              try { await api.post(`/agency/${agencyId}/users/${u.id}/send-reset-link`); toast.success(`Password link emailed to ${u.email}`); }
                              catch (e) { toast.error(e.response?.data?.detail || "Could not send link"); }
                            }}>
                            <Mail size={12} className="mr-1"/> Password link
                          </Button>
                          {u.id !== me?.id && u.role !== "admin" && (
                            <Button size="sm" variant="outline" onClick={() => remove(u)} className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50 text-xs" data-testid={`agency-user-remove-${u.id}`}>
                              <Trash2 size={12}/>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#6C757D]">No users in this agency yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </main>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, mode: "create", editing: null })}>
        <DialogContent className="sm:max-w-2xl" data-testid="agency-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0B132B]">
              {dialog.mode === "edit" ? "Edit user" : "Add user"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6C757D]">
              {agency?.name} · {dialog.mode === "edit" ? "Update role, visibility scope, or dealer assignments." : "Assign role and choose whether the user sees all agency data or only assigned dealers."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="agency-user-name"/>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required data-testid="agency-user-email"/>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="agency-user-role"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="agency_admin" disabled={!canCreateAgencyAdmin && form.role !== "agency_admin"}>
                      Agency Admin{!canCreateAgencyAdmin && " (needs super admin)"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">
                  {dialog.mode === "edit" ? "New password (optional)" : "Password"}
                </Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                  placeholder={dialog.mode === "edit" ? "Leave blank to keep current" : "Min. 6 characters"}
                  required={dialog.mode === "create"} data-testid="agency-user-password"/>
              </div>
            </div>

            {form.role !== "agency_admin" && (
              <>
                <label className="flex items-start gap-3 p-3 border border-[#E9ECEF] rounded-sm cursor-pointer hover:border-[#0B132B]/40" data-testid="agency-user-commissions-toggle">
                  <input type="checkbox" checked={!!form.can_view_commissions} onChange={(e) => setForm({ ...form, can_view_commissions: e.target.checked })} className="mt-0.5" />
                  <span><span className="block text-sm font-bold text-[#0B132B]">Commissions & contracts access</span>
                    <span className="block text-[11px] text-[#6C757D] mt-0.5">Shows the Contracts and Commissions dashboard tiles and the Commissions module for this agent.</span></span>
                </label>
                <div className="grid gap-2">
                  <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Visibility scope</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setForm({...form, visibility_scope: "assigned"})}
                      className={`p-3 text-left border rounded-sm transition-colors ${form.visibility_scope === "assigned" ? "border-[#0B132B] bg-[#0B132B]/5" : "border-[#E9ECEF] bg-white hover:border-[#0B132B]/40"}`}
                      data-testid="scope-assigned">
                      <div className="flex items-center gap-2 text-sm font-bold text-[#0B132B]"><EyeOff size={13}/> Assigned only</div>
                      <div className="text-[11px] text-[#6C757D] mt-1">Sees only dealers you assign below + their own proposals.</div>
                    </button>
                    <button type="button" onClick={() => setForm({...form, visibility_scope: "all"})}
                      className={`p-3 text-left border rounded-sm transition-colors ${form.visibility_scope === "all" ? "border-[#0B132B] bg-[#0B132B]/5" : "border-[#E9ECEF] bg-white hover:border-[#0B132B]/40"}`}
                      data-testid="scope-all">
                      <div className="flex items-center gap-2 text-sm font-bold text-[#0B132B]"><Eye size={13}/> All agency data</div>
                      <div className="text-[11px] text-[#6C757D] mt-1">Sees every dealer and every proposal across your agency.</div>
                    </button>
                  </div>
                </div>

                {form.visibility_scope === "assigned" && (
                  <div className="grid gap-2">
                    <Label className="text-xs uppercase tracking-[0.16em] font-bold text-[#0B132B]">Assigned dealers</Label>
                    {dealers.length === 0 ? (
                      <div className="p-4 text-xs text-[#6C757D] border border-dashed border-[#E9ECEF] rounded-sm text-center italic">
                        No dealers in your agency yet. Add dealers on the Dealers page, then return here to assign them.
                      </div>
                    ) : (
                      <div className="max-h-[220px] overflow-y-auto border border-[#E9ECEF] rounded-sm" data-testid="agency-user-dealers">
                        {dealers.map((d) => {
                          const checked = form.assigned_dealer_ids.includes(d.id);
                          return (
                            <label key={d.id} className={`flex items-start gap-3 p-3 border-b border-[#E9ECEF] cursor-pointer transition-colors ${checked ? "bg-[#D4AF37]/5" : "hover:bg-[#F8F9FA]"}`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleDealerAssign(d.id)} className="mt-0.5"/>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold text-[#0B132B] truncate">{d.dealer_name}</div>
                                <div className="text-[11px] text-[#6C757D] truncate">{d.contact_email}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog({ open: false, mode: "create", editing: null })} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0B132B] hover:bg-[#0B132B]/90" data-testid="agency-user-submit">
                {saving && <Loader2 size={14} className="mr-2 animate-spin"/>}
                {dialog.mode === "edit" ? "Save changes" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
